import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  MatchingBoardItemDto,
  MatchingBoardPageDto,
  MatchingEventViewDto,
  MatchingMetaDto,
  MatchingPanelDayDto,
} from '../../api/matchingTypes';

type MatchingState = {
  /** Shared on/off switch of the feature; null until the first load. */
  enabled: boolean | null;
  meta: MatchingMetaDto | null;
  board: {
    items: MatchingBoardItemDto[];
    page: number;
    totalPages: number;
    totalElements: number;
    isLoading: boolean;
  };
  /** Per-date panel data, keyed by YYYY-MM-DD. */
  panelByDate: Record<string, MatchingPanelDayDto>;
  /** Batch event projections, keyed by event id. Kept apart from ordinary events. */
  eventViewsById: Record<string, MatchingEventViewDto>;
  /** Last known versions of own requests (from create/update responses), used for If-Match. */
  requestVersions: Record<string, number>;
};

const initialState: MatchingState = {
  enabled: null,
  meta: null,
  board: { items: [], page: 0, totalPages: 0, totalElements: 0, isLoading: false },
  panelByDate: {},
  eventViewsById: {},
  requestVersions: {},
};

const matchingSlice = createSlice({
  name: 'matching',
  initialState,
  reducers: {
    setEnabled(state, action: PayloadAction<boolean>) {
      state.enabled = action.payload;
    },
    setMeta(state, action: PayloadAction<MatchingMetaDto>) {
      state.meta = action.payload;
    },
    setBoardLoading(state, action: PayloadAction<boolean>) {
      state.board.isLoading = action.payload;
    },
    /** Page 0 replaces the list, later pages are appended. */
    setBoardPage(state, action: PayloadAction<MatchingBoardPageDto>) {
      const { items, page, totalPages, totalElements } = action.payload;
      state.board.items = page === 0 ? items : [...state.board.items, ...items];
      state.board.page = page;
      state.board.totalPages = totalPages;
      state.board.totalElements = totalElements;
    },
    setPanel(state, action: PayloadAction<MatchingPanelDayDto[]>) {
      state.panelByDate = Object.fromEntries(action.payload.map((d) => [d.date, d]));
    },
    setEventViews(state, action: PayloadAction<MatchingEventViewDto[]>) {
      for (const view of action.payload) {
        state.eventViewsById[String(view.eventId)] = view;
      }
    },
    setRequestVersion(state, action: PayloadAction<{ id: number; version: number }>) {
      state.requestVersions[String(action.payload.id)] = action.payload.version;
    },
    resetMatching() {
      return initialState;
    },
  },
});

export const matchingReducer = matchingSlice.reducer;
export const matchingActions = matchingSlice.actions;
